import { Logo } from "@/components/brand/Logo";
import type { ReactNode } from "react";
import { LogOut } from "lucide-react";
import { BarraRitorno } from "@/components/layout/BarraRitorno";
import { invitiAllAcquistoNellApp } from "@/lib/iscrizione";

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Per le pagine che non si possono saltare (scegli la password, completa il
   * profilo): li' «Indietro» girava in tondo, perche' ProtectedRoute riporta
   * sempre li'. L'uscita vera e' uscire dall'account: la pagina passa la sua
   * funzione (useAuth().signOut + /login), e in cima compare «Esci». Qui non
   * si importa useAuth: privacy e termini usano questa cornice senza login.
   */
  onEsci?: () => void;
}

/**
 * L'uscita in cima, ferma mentre si scorre (06/10/2026, tasti indietro su
 * tutto). Prima stava dentro il riquadro e su Iscrizione, Privacy e Termini
 * spariva scorrendo; e decideva con window.history.length, che conta anche le
 * pagine di altri siti: aprendo l'app da un link il tasto non faceva niente.
 */
function UscitaInCima({ onEsci }: { onEsci?: () => void }) {
  if (onEsci) return <BarraEsci esci={onEsci} />;
  return <BarraRitorno madre="/" nomeMadre="Home" larghezza="max-w-md" className="w-full self-stretch" />;
}

function BarraEsci({ esci }: { esci: () => void }) {
  return (
    <div className="topbar-safe w-full self-stretch border-b border-border/40">
      <div className="mx-auto flex w-full max-w-md items-center px-4 md:px-6">
        <button
          type="button"
          onClick={esci}
          className="-ml-2 inline-flex min-h-12 items-center gap-1.5 rounded-lg px-2 text-sm text-muted-foreground transition-colors hover:bg-surface hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          Esci
        </button>
      </div>
    </div>
  );
}

export function AuthLayout({ title, subtitle, children, footer, onEsci }: AuthLayoutProps) {
  return (
    // CENTRATO CON my-auto, NON CON justify-center (01/10/2026, screenshot di
    // Apple): un riquadro piu' alto dello schermo (l'iscrizione) con
    // justify-center sborda IN ALTO, oltre il margine della tacca, e
    // «Indietro» finiva attaccato all'orologio. my-auto centra quando c'e'
    // posto e, quando non c'e', parte dall'alto sotto la tacca.
    // E IL RESPIRO STA DENTRO, NON SULLA RADICE: un py-8 accanto a page-safe
    // le sostituisce il padding della tacca (32 px al posto di 59) e
    // «Indietro» finiva sotto l'orologio. Misurato il 01/10 a 390x844.
    <div className="page-safe flex min-h-[100dvh] flex-col items-center bg-background">
      <UscitaInCima onEsci={onEsci} />
      <div className="my-auto w-full max-w-md px-4 py-8">
        <div className="relative rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
          <div className="mb-6 flex flex-col items-center text-center">
            <Logo size="lg" />
            <h1 className="mt-4 text-xl font-semibold tracking-tight text-foreground">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
            ) : (
              /* NON PIU' "Beta privata" (26/08/2026). Questa riga sta sotto il
                 logo di OGNI pagina di accesso: e' la primissima cosa che legge
                 un revisore di Apple, e le linee guida escludono dallo store le
                 versioni di prova. Il commento in Settings dava per tolto questo
                 banner: non era vero, era rimasto qui. Il senso resta - il
                 servizio e' per professionisti - senza la parola che invita al
                 rifiuto. */
              <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
                Riservato agli installatori professionisti
              </p>
            )}
          </div>
          {children}
          {footer ? <div className="mt-6 text-center text-sm">{footer}</div> : null}
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Hai problemi?{" "}
          {/* iPhone (Apple 3.1.1): sotto un modulo d'iscrizione «Contatta
              Datacom» si legge come «contatta per comprare» (era nello
              screenshot del rifiuto del 29/09). Li' e' solo assistenza. */}
          {invitiAllAcquistoNellApp() ? (
            <a href="mailto:u.chiatti@datacomtecnologie.it" className="text-primary hover:underline">
              Contatta Datacom
            </a>
          ) : (
            <a href="mailto:supporto@datacomtecnologie.it" className="text-primary hover:underline">
              Scrivi all&apos;assistenza
            </a>
          )}
        </p>
      </div>
    </div>
  );
}